import { Layers, TrendingUp, Users, Image, ShieldCheck, Star, Zap } from "lucide-react";

// P3 Checkpoint 2: Email View + contextual-filter configuration.
//
// Purely structural/config — no P1 schema, no real option sources. Each
// view's `contextual` list names filters a later checkpoint will wire to
// real data; for now they exist only so the View selector and Filters
// control can be built once, against a stable shape, and reused as each
// view gains real analytics without restructuring the Email header.
// Creative, Deliverability, and Reputation stay empty until their data
// contracts are characterized.
export const EMAIL_VIEWS = [
  {
    id: "overview",
    label: "Overview",
    description: "High-level performance summary",
    icon: Layers,
  },
  {
    id: "performance",
    label: "Performance",
    description: "Detailed performance metrics",
    icon: TrendingUp,
  },
  {
    id: "audience",
    label: "Audience & Acquisition",
    description: "Audience and acquisition analysis",
    icon: Users,
  },
  // DASH-EMAIL-006a (NAME-1): the prior label/description ("Creative /
  // Asset Performance" / "Creative effectiveness") implied a generalized
  // image/video/asset-performance system, and the implemented view was
  // (and structurally still is) broadcast-scoped subject-line/preview-
  // text/template-variant analysis only.
  //
  // DASH-EMAIL-010 (§7): EMAIL-ANALYTICS-SPEC-001 §20 freezes "Creative
  // Analytics" as the canonical term for observed performance by Angle,
  // Creative, Creative Version/Fingerprint, and Asset Version — a broader
  // future scope than the current subject-line-only surface. Renamed from
  // "Creative — Subject Line Performance" to match that frozen vocabulary;
  // the description no longer implies subject line is the whole future
  // scope of this view (the multi-grain rebuild itself is a separate,
  // not-yet-authorized checkpoint — see EMAIL-ANALYTICS-SPEC-001 §15/§20).
  {
    id: "creative",
    label: "Creative Analytics",
    description: "Observed creative and messaging performance across sent broadcasts",
    icon: Image,
  },
  {
    id: "deliverability",
    label: "Deliverability & Hygiene",
    description: "Delivery and recipient health",
    icon: ShieldCheck,
  },
  {
    id: "reputation",
    label: "Reputation",
    description: "Sender reputation and scoring",
    icon: Star,
  },
  // P1 SLICE 3: the eight frozen Behavioral LPE action metrics (GET
  // /email-delivery/dashboard/behavioral-actions, P1 IMPLEMENTATION
  // SLICE 2). User-facing tab label is deliberately the short "Actions" --
  // "Behavioral Actions" is the backend/architecture-freeze name for this
  // reporting surface, not the customer-facing label. A sibling of
  // Overview/Performance, never folded into either.
  {
    id: "behavioral",
    label: "Actions",
    description: "Online enrollment, agent assistance, and save quote actions",
    icon: Zap,
  },
];

// Persistent controls (Date Range, Environment) apply to every view and are
// owned by EmailDashboardPanel directly. This config only names each
// view's *contextual* filters — config-driven placeholders in Checkpoint 2,
// not functional pickers, since no real option source exists yet.
//
// Checkpoint 3.2: Performance's Program/Campaign/Broadcast filters became
// real, always-visible controls owned by EmailPerformanceView itself
// (not the header's EmailFiltersControl popover, since a 3-level cascading
// filter that changes the whole view's data needs its selection visible at
// a glance). `contextual` is intentionally empty here now so the header's
// Filters popover doesn't show a stale, redundant "Program/Campaign/
// Broadcast — Coming soon" alongside the real inline controls.
export const EMAIL_VIEW_FILTER_CONFIG = {
  overview: { contextual: [] },
  performance: { contextual: [] },
  // DASH-EMAIL-006a (DEAD-2): the "audience" contextual key (label
  // "Audience") predated DASH-EMAIL-004's real data contract, had no
  // matching draft field, and was left as a permanent, unreachable
  // "Coming soon" row — every real dimension this view segments by
  // (leadSource/acquisitionChannel/geography/lifecycleState) is already
  // covered below; there was no remaining meaning to give it. Removed
  // rather than replaced with another invented dimension.
  //
  // DASH-EMAIL-P1-IMPL-008: those four dimensions are now empty too —
  // DASH-EMAIL-P1-CHAR-007 found the entire "audience segmentation"
  // concept has no authoritative backend source (a hardcoded fixture, not
  // a shell phase for a real endpoint). Leaving their popover rows in
  // place would be exactly the "interactive control that appears to
  // manipulate authoritative metrics when none exist" this checkpoint
  // requires removing. EmailFiltersControl.jsx already renders nothing at
  // all when `contextual` is empty (the same mechanism Overview/
  // Performance already rely on), so this is the smallest possible
  // suppression — no change to EmailFiltersControl.jsx itself needed.
  audience: { contextual: [] },
  // DASH-EMAIL-011a: Creative Analytics' one real contextual dimension.
  // Retires DASH-EMAIL-005b's template_variant key — no governed
  // equivalent existed for it. Angle's real option list is fetched from
  // the Email Marketing catalog since DASH-EMAIL-012A (see
  // EmailDashboardPanel.jsx / EmailFiltersControl.jsx).
  creative: { contextual: [{ key: "angle", label: "Angle" }] },
  deliverability: { contextual: [] },
  reputation: { contextual: [] },
};

export function getEmailViewFilters(viewId) {
  return EMAIL_VIEW_FILTER_CONFIG[viewId]?.contextual || [];
}

export default EMAIL_VIEWS;
